import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import SiteHeader from '@/components/feature/SiteHeader';
import Footer from '@/pages/home/components/Footer';
import GlowButton from '@/components/base/GlowButton';
import ContactModal from './components/ContactModal';

const contactCards = [
  {
    icon: 'ri-briefcase-4-line',
    iconBg: 'bg-sky-100/90',
    iconColor: 'text-sky-500',
    tag: 'Business',
    tagBg: 'bg-sky-100/80 text-sky-600 border border-sky-200/70',
    title: 'Business Manager',
    desc: "Interested in partnerships, co-marketing, or platform collaborations? Reach out to our business team and let's explore opportunities together.",
    points: ['Strategic partnerships', 'Co-marketing campaigns', 'Platform integrations'],
    telegram: 'liketgli',
    cta: 'Chat with Business Team',
    cardBg: 'bg-gradient-to-br from-sky-100/75 via-sky-50/65 to-sky-50/50',
    cardBorder: 'border-sky-300/50',
    cardHover: 'hover:border-sky-400/60 hover:from-sky-100/90 hover:via-sky-50/80 hover:to-sky-50/65',
    glowColor: 'rgba(14,165,233,0.18)',
    dividerColor: 'border-sky-200/70',
    checkColor: 'text-sky-500',
    taglineColor: 'text-sky-500',
    accent: '#0ea5e9',
  },
  {
    icon: 'ri-advertisement-line',
    iconBg: 'bg-amber-100/90',
    iconColor: 'text-amber-500',
    tag: 'Advertising',
    tagBg: 'bg-amber-100/80 text-amber-600 border border-amber-200/70',
    title: 'Ad Placements',
    desc: 'Want to promote your product or service to thousands of Telegram community enthusiasts? Our ad placements offer premium visibility.',
    points: ['Banner & sponsored slots', 'Featured community listings', 'Newsletter sponsorship'],
    telegram: 'liketgli',
    cta: 'Get Advertising Info',
    cardBg: 'bg-gradient-to-br from-amber-100/75 via-amber-50/65 to-amber-50/50',
    cardBorder: 'border-amber-300/50',
    cardHover: 'hover:border-amber-400/60 hover:from-amber-100/90 hover:via-amber-50/80 hover:to-amber-50/65',
    glowColor: 'rgba(245,158,11,0.18)',
    dividerColor: 'border-amber-200/70',
    checkColor: 'text-amber-500',
    taglineColor: 'text-amber-500',
    accent: '#f59e0b',
  },
  {
    icon: 'ri-links-line',
    iconBg: 'bg-emerald-100/90',
    iconColor: 'text-emerald-500',
    tag: 'Link Exchange',
    tagBg: 'bg-emerald-100/80 text-emerald-600 border border-emerald-200/70',
    title: 'Friendly Links',
    desc: 'Looking to exchange links with TGDirectory? We welcome quality websites and directories to grow together through mutual link partnerships.',
    points: ['Reciprocal link exchange', 'Directory cross-promotion', 'SEO-friendly placements'],
    telegram: 'liketgli',
    cta: 'Apply for Link Exchange',
    cardBg: 'bg-gradient-to-br from-emerald-100/75 via-emerald-50/65 to-emerald-50/50',
    cardBorder: 'border-emerald-300/50',
    cardHover: 'hover:border-emerald-400/60 hover:from-emerald-100/90 hover:via-emerald-50/80 hover:to-emerald-50/65',
    glowColor: 'rgba(16,185,129,0.18)',
    dividerColor: 'border-emerald-200/70',
    checkColor: 'text-emerald-500',
    taglineColor: 'text-emerald-500',
    accent: '#10b981',
  },
];

const faqs = [
  { q: 'How quickly will you respond?', a: 'Our team typically replies within 24 hours on business days. For urgent matters, mention it in your first message.' },
  { q: 'What information should I prepare?', a: 'For ads, share your target audience and budget. For partnerships, briefly describe your platform. For link exchange, provide your site URL and DA.' },
  { q: 'Are there minimum requirements for ad placements?', a: 'We work with businesses of all sizes. Contact us for a customized package that fits your goals and budget.' },
  { q: 'How do I apply for a friendly link?', a: 'Simply message us on Telegram with your site URL, description, and the page where you\'d place our link. We\'ll review and get back to you.' },
];

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition = `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        observer.unobserve(el);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}

export default function ContactPage() {
  const navigate = useNavigate();
  useSEO({
    title: 'Contact Us – Advertise & Partner with TGDirectory',
    description: 'Get in touch with TGDirectory for advertising placements, business partnerships, and friendly link exchanges. Reach 50,000+ daily Telegram community seekers.',
    keywords: 'TGDirectory contact, advertise Telegram directory, Telegram community partnership, link exchange',
    canonical: 'https://tgdirectory.app/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact TGDirectory',
      url: 'https://tgdirectory.app/contact',
      description: 'Contact TGDirectory for advertising, partnerships, and link exchange.',
    },
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [contactModal, setContactModal] = useState<number | null>(null);

  const card0Ref = useReveal(0);
  const card1Ref = useReveal(100);
  const card2Ref = useReveal(200);
  const bannerRef = useReveal(0);
  const faqRef = useReveal(0);

  const cardRefs = [card0Ref, card1Ref, card2Ref];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <SiteHeader showBack />

      {/* Hero */}
      <section className="pt-14 pb-10 px-6 md:px-10 lg:px-14 text-center">
        <div className="max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0088CC]/10 backdrop-blur-sm border border-[#0088CC]/20 text-[#0088CC] text-xs font-semibold rounded-full mb-4">
            <i className="ri-message-3-line text-sm"></i>
            Get in Touch
          </span>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4 leading-tight">
            Let's Work Together
          </h1>
          <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-8">
            Whether you're looking to advertise, partner with us, or exchange links — we'd love to hear from you. Pick the right channel below and we'll get back to you quickly.
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl mx-auto">
            {[
              { icon: 'ri-eye-line', val: '50k+', label: 'Daily Visitors' },
              { icon: 'ri-group-line', val: '200k+', label: 'Monthly Users' },
              { icon: 'ri-global-line', val: '80+', label: 'Countries' },
              { icon: 'ri-shake-hands-line', val: '120+', label: 'Partners' },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white/50 backdrop-blur-md border border-white/80 rounded-xl px-3 py-3 flex flex-col items-center gap-1"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0088CC]/10 border border-[#0088CC]/15 mb-0.5">
                  <i className={`${s.icon} text-[#0088CC] text-sm`}></i>
                </span>
                <p className="text-base font-extrabold text-slate-800 leading-none">{s.val}</p>
                <p className="text-[10px] text-slate-400 whitespace-nowrap">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="max-w-screen-lg mx-auto px-6 md:px-10 lg:px-14 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {contactCards.map((card, idx) => (
            <div
              key={card.title}
              ref={cardRefs[idx]}
              className={`relative ${card.cardBg} ${card.cardHover} backdrop-blur-2xl rounded-2xl border ${card.cardBorder} p-6 flex flex-col transition-all duration-300 overflow-hidden cursor-pointer`}
              style={{ WebkitBackdropFilter: 'blur(32px) saturate(160%)', backdropFilter: 'blur(32px) saturate(160%)' }}
              onClick={() => setContactModal(idx)}
            >
              {/* Noise / grain texture overlay for frosted glass feel */}
              <div
                className="absolute inset-0 pointer-events-none rounded-2xl"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'repeat',
                  opacity: 0.55,
                }}
              />
              {/* Large radial glow — top-right */}
              <div
                className="absolute -top-10 -right-10 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${card.glowColor} 0%, transparent 65%)` }}
              />
              {/* Smaller secondary glow — bottom-left */}
              <div
                className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${card.glowColor} 0%, transparent 70%)` }}
              />
              {/* Top edge highlight — stronger */}
              <div
                className="absolute top-0 left-[10%] right-[10%] h-[1.5px] pointer-events-none"
                style={{ background: `linear-gradient(90deg, transparent, ${card.accent}55, transparent)` }}
              />
              {/* Inner top white sheen */}
              <div
                className="absolute top-0 left-0 right-0 h-16 pointer-events-none rounded-t-2xl"
                style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.28), transparent)' }}
              />

              {/* Icon + Tag */}
              <div className="flex items-start justify-between mb-4 relative">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.iconBg} border border-white/80 relative`} style={{ WebkitBackdropFilter: 'blur(12px)', backdropFilter: 'blur(12px)' }}>
                  <i className={`${card.icon} text-xl ${card.iconColor}`}></i>
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm ${card.tagBg}`}>
                  {card.tag}
                </span>
              </div>

              {/* Title & Desc */}
              <h2 className="text-base font-bold text-slate-800 mb-2 relative">{card.title}</h2>
              <p className="text-sm text-slate-500 leading-relaxed mb-4 relative">{card.desc}</p>

              {/* Divider */}
              <div className={`w-full h-px mb-4 border-t ${card.dividerColor} relative`} style={{ boxShadow: `0 1px 0 rgba(255,255,255,0.6)` }} />

              {/* Points */}
              <ul className="flex flex-col gap-2.5 mb-6 flex-1 relative">
                {card.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-slate-500">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className={`ri-checkbox-circle-line text-base ${card.checkColor}`}></i>
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <GlowButton
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); setContactModal(idx); }}
                className="w-full py-2.5 text-sm font-semibold rounded-xl relative"
              >
                <i className="ri-telegram-fill text-base"></i>
                {card.cta}
              </GlowButton>
              <p className={`text-center text-[11px] mt-2 ${card.taglineColor}`}>@{card.telegram}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Contact Banner */}
      <section className="max-w-screen-lg mx-auto px-6 md:px-10 lg:px-14 pb-12">
        <div ref={bannerRef} className="bg-[#0088CC]/8 backdrop-blur-md border border-[#0088CC]/20 rounded-2xl px-6 py-7 flex flex-col sm:flex-row items-center gap-5">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0088CC]/15 border border-[#0088CC]/20 shrink-0">
            <i className="ri-telegram-fill text-[#0088CC] text-xl"></i>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-sm font-bold text-slate-800 mb-0.5">Prefer to reach out directly?</h3>
            <p className="text-xs text-slate-500">All inquiries go through one Telegram account — fast, easy, and no inbox clutter.</p>
          </div>
          <GlowButton
            as="a"
            href="https://t.me/liketgli"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-sm font-bold px-5 py-2.5 rounded-xl shrink-0 whitespace-nowrap"
          >
            <i className="ri-telegram-fill text-base"></i>
            @liketgli
          </GlowButton>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-screen-lg mx-auto px-6 md:px-10 lg:px-14 pb-16">
        <h3 className="text-base font-bold text-slate-700 mb-5 text-center">Frequently Asked Questions</h3>
        <div ref={faqRef} className="flex flex-col gap-2.5 max-w-2xl mx-auto">
          {faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={faq.q}
                className={`bg-white/55 backdrop-blur-md rounded-xl border transition-all duration-200 overflow-hidden ${
                  isOpen ? 'border-[#0088CC]/30 bg-white/70' : 'border-white/80 hover:border-[#b8d4ea]/60'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left cursor-pointer"
                >
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 transition-colors ${isOpen ? 'bg-[#0088CC]/15' : 'bg-slate-100/80'}`}>
                    <i className={`ri-question-line text-sm ${isOpen ? 'text-[#0088CC]' : 'text-slate-400'}`}></i>
                  </span>
                  <span className={`text-sm font-semibold flex-1 transition-colors ${isOpen ? 'text-[#0088CC]' : 'text-slate-700'}`}>
                    {faq.q}
                  </span>
                  <span className={`w-5 h-5 flex items-center justify-center shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-slate-400 text-base"></i>
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? '200px' : '0px', opacity: isOpen ? 1 : 0 }}
                >
                  <p className="text-sm text-slate-500 leading-relaxed px-5 pb-4 pl-14">
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      <ContactModal
        open={contactModal !== null}
        card={contactModal !== null ? contactCards[contactModal] : null}
        onClose={() => setContactModal(null)}
      />
    </div>
  );
}
